import { AsyncPipe } from '@angular/common';
import { httpResource } from '@angular/common/http';
import { AfterContentInit, ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';
import { RouterOutlet } from '@angular/router';
import { MatomoTracker } from 'ngx-matomo-client';
import { distinctUntilChanged, filter, mergeMap, startWith, tap } from 'rxjs';

import { LazySpriteSheetComponent } from '@dmb/core';
import {
  CoBrandingService,
  DmbFontSize,
  DmbFontSizes,
  I18n,
  StorageService,
  UserProfile,
  UserService,
  dmbThemes,
  isFontSize,
} from '@dmb/dmb-ui';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'dmb-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [LazySpriteSheetComponent, RouterOutlet, AsyncPipe],
})
export class AppComponent implements AfterContentInit {
  private userService = inject(UserService);
  private storage = inject(StorageService);
  private i18n = inject(I18n);
  private cobrandingService = inject(CoBrandingService);
  private sanitizer = inject(DomSanitizer);
  private tracker = inject(MatomoTracker);

  spriteSheet = httpResource.text(() => '/assets/dmb-icon-sprite.svg', {
    parse: (value) => this.sanitizer.bypassSecurityTrustHtml(value),
    defaultValue: '',
  });

  constructor() {
    this.userService.fontSize
      .pipe(
        filter((f) => !!f),
        startWith(this.getDefaultFontSize()),
        takeUntilDestroyed(),
      )
      .subscribe((fs: DmbFontSize) => this.setFontSize(fs));
    this.theme.pipe(takeUntilDestroyed()).subscribe((t) => this.switchTheme(t));

    this.userService.language
      .pipe(
        mergeMap((l) => this.i18n.setLocale(l)),
        takeUntilDestroyed(),
      )
      .subscribe((reloadRequired) => {
        if (reloadRequired) location.reload();
      });
    this.userService.user.pipe(takeUntilDestroyed()).subscribe((u) => this.setTrackingInfo(u));
  }

  setTrackingInfo(u: UserProfile) {
    this.tracker.setCustomVariable(2, 'userFunction', u.userFunction, 'page');
  }

  ngAfterContentInit(): void {
    const link: HTMLLinkElement = document.querySelector("link[rel~='icon']") || document.createElement('link');
    link.rel = 'icon';
    link.href = `/assets/favicons/${this.cobrandingService.favicon}`;

    document.head.appendChild(link);
  }

  theme = this.userService.theme.pipe(
    startWith(this.getDefaultTheme()),
    distinctUntilChanged(),
    tap((t) => {
      this.storeTheme(t);
    }),
  );

  getDefaultFontSize(): DmbFontSize {
    try {
      if (!this.storage.localStorage?.getItem('fontSize')) return 'regular';
      if (isFontSize(this.storage.localStorage?.getItem('fontSize'))) {
        return this.storage.localStorage?.getItem('fontSize') as DmbFontSize;
      }
      return 'regular';
    } catch {
      return 'regular';
    }
  }

  getDefaultTheme(): string {
    try {
      if (!this.storage.localStorage?.getItem('theme')) return 'standard';
      return this.storage.localStorage?.getItem('theme') as string;
    } catch {
      return 'standard';
    }
  }

  setFontSize(size: DmbFontSize) {
    try {
      this.storage.localStorage?.setItem('fontSize', size);
    } catch {
      console.log('Unable to set in localstorage, allow 3rd party cookies to gain feature');
    }

    document.documentElement.style.setProperty('--app-base-size', `var(--app-base-size-${size})`);
    DmbFontSizes.forEach((fs) => document.documentElement.classList.toggle(fs, false));
    document.documentElement.classList.toggle(size, true);
  }

  switchTheme(theme: string) {
    const fName = `theme-${theme}.css`;
    const current = document?.getElementById('theme')?.getAttribute('href');
    if (current !== `theme-${theme}.css`) document?.getElementById('theme')?.setAttribute('href', fName);
    dmbThemes.forEach((th) => document.documentElement.classList.toggle(`theme-${th}`, false));
    document.documentElement.classList.toggle(`theme-${theme}`, true);
  }

  private storeTheme(t: string) {
    try {
      this.storage.localStorage?.setItem('theme', t);
    } catch {
      console.log('Unable to set in localstorage, allow 3rd party cookies to gain feature');
    }
  }
}
